import {
  fetchBanners,
  fetchMarketById,
  fetchPage,
  fetchPageLinks,
  FormattedRichMessage,
  isEmail,
  localeToMarketCode,
  MutateState,
  PageProps,
} from '@instabox/consumer-web-shared'
import {
  Button,
  FeedbackMessage,
  Heading,
  InputField,
  Stack,
  Text,
} from '@instabox/consumer-web-ui'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'
import axios from 'axios'
import { AnimatePresence } from 'framer-motion'
import { GetStaticProps, NextPage } from 'next'
import { ChangeEvent, useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import feedbackMessageAnimation from '../animations/feedbackMessageAnimation'
import AnimatedCard from '../components/AnimatedCard/AnimatedCard'
import { ENV_CONFIG } from '../constants/envConfig'
import useAnalytics from '../hooks/useAnalytics'
import styles from './search.module.scss'

const SLUG = 'search'

function fromFormValues({ id, code, token }) {
  const trimmedId = id.trim()
  const trimmedCode = code.trim()

  return {
    id: trimmedId,
    email: isEmail(trimmedCode) ? trimmedCode : '',
    phoneNumber: isEmail(trimmedCode) ? '' : trimmedCode,
    token,
  }
}

export interface SearchPageProps extends PageProps {}

const SearchPage: NextPage<SearchPageProps> = ({ content }) => {
  const [image] = content?.filter(({ item }) => item.__typename === 'images') ?? []
  const { formatMessage } = useIntl()
  const [mutateState, setMutateState] = useState<MutateState>(MutateState.IDLE)
  const [form, setForm] = useState({ code: '', id: '' })
  const [mounted, setMounted] = useState(false)
  const heroRef = useRef<HTMLDivElement | null>(null)
  const turnstileRef = useRef<TurnstileInstance | null>(null)
  const sendAnalyticsEvent = useAnalytics()

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (image?.item.__typename === 'images') {
      const url = `${ENV_CONFIG.DIRECTUS_URL}/assets/${image.item?.image?.id}`
      heroRef.current.style.setProperty('--background-image', `url(${url})`)
    }
  }, [image])

  const handleChange = ({ target: { name, value } }: ChangeEvent<HTMLInputElement>) => {
    setForm({
      ...form,
      [name]: value,
    })
  }

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      try {
        setMutateState(MutateState.PROCESSING)
        sendAnalyticsEvent('search_for_parcel')
        const token = turnstileRef.current?.getResponse()

        if (!token) {
          throw new Error('Turnstile token is missing')
        }

        const { data } = await axios.post('/api/authenticate', fromFormValues({ ...form, token }))

        if (data.redirectTo) {
          window.location.href = data.redirectTo
          setMutateState(MutateState.SUCCESS)
        } else {
          setMutateState(MutateState.ERROR)
        }
      } catch {
        setMutateState(MutateState.ERROR)
      }
    },
    [form, sendAnalyticsEvent],
  )

  const isCodeValid = form.code?.length >= 4
  const isIdValid = form.id?.length >= 4
  const isFormValid = isCodeValid && isIdValid

  return (
    <div className={styles.container}>
      <div className={styles.hero} ref={heroRef}>
        <FormHeading />
      </div>
      <div className={styles.form}>
        <Stack gap="xxl">
          <Stack gap="xl">
            <Stack align="center" gap="lg">
              <FormHeading />
              <Text as="div" color="white" align="center">
                {mounted && <FormattedRichMessage id="search.form.subtitle" />}
              </Text>
            </Stack>
            <form onSubmit={handleSubmit} noValidate>
              <Turnstile
                ref={turnstileRef}
                siteKey={ENV_CONFIG.TURNSTILE_SITE_KEY}
                options={{ size: 'invisible' }}
              />
              <Stack gap="lg">
                <Stack className={styles.fields} gap="sm">
                  <InputField
                    label={formatMessage({ id: 'search.form.input_parcel' })}
                    name="id"
                    id="id"
                    value={form.id}
                    noBorder
                    className={mutateState === MutateState.ERROR && styles.shake}
                    onChange={handleChange}
                    textVariant="uppercase"
                  />
                  <InputField
                    label={formatMessage({ id: 'search.form.input_hint' })}
                    name="code"
                    id="code"
                    value={form.code}
                    noBorder
                    className={mutateState === MutateState.ERROR && styles.shake}
                    onChange={handleChange}
                  />
                </Stack>
                <Stack align="center" gap="md">
                  <Button
                    type="submit"
                    variant="solid"
                    disabled={!isFormValid || mutateState === MutateState.PROCESSING}
                    isLoading={mutateState === MutateState.PROCESSING}>
                    <FormattedMessage id="search.form.button_submit" />
                  </Button>
                  <AnimatePresence>
                    {mutateState === MutateState.ERROR && (
                      <AnimatedCard size="sm" variant="info" {...feedbackMessageAnimation}>
                        <FeedbackMessage>
                          <FormattedMessage id="search.form.error_message" />
                        </FeedbackMessage>
                      </AnimatedCard>
                    )}
                  </AnimatePresence>
                </Stack>
              </Stack>
            </form>
          </Stack>
        </Stack>
      </div>
    </div>
  )
}

export default SearchPage

const FormHeading = () => (
  <Heading color="white" align="center" fontSize="xl" className={styles.formHeading}>
    <FormattedMessage id="search.form.heading" />
  </Heading>
)

export const getStaticProps: GetStaticProps<SearchPageProps> = async ({ locale }) => {
  const {
    data: { marketById },
  } = await fetchMarketById(localeToMarketCode(locale))
  const page = await fetchPage(locale, SLUG)
  const pageLinks = await fetchPageLinks(locale)
  const banners = await fetchBanners(locale, SLUG)

  return {
    props: {
      market: marketById,
      pageLinks,
      fullBleed: true,
      banners,
      ...page,
    },
    revalidate: 60,
  }
}
